import dayjs from "dayjs";
import { UCUM_STEPS } from "@/services/fhir/observation/wearableObservationBuilder";

/** Duração e período do sono (LOINC 93832-4). */
export function getSleepTotalDisplay(resource: any) {
  if (!resource) {
    return {
      durationText: "-",
      periodText: "",
      noteText: "",
      start: undefined as string | undefined,
      end: undefined as string | undefined,
    };
  }

  const value = resource?.valueQuantity?.value;
  const unit = resource?.valueQuantity?.unit || resource?.valueQuantity?.code;

  let durationText = "-";

  if (value !== undefined && (unit === "min" || unit === "s")) {
    if (unit === "min") {
      const totalMinutes = Math.round(value as number);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      durationText = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
    } else if (unit === "s") {
      const totalSeconds = Math.round(value as number);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      durationText = minutes > 0 ? `${minutes}min ${seconds}s` : `${seconds}s`;
    }
  }

  const start = resource?.effectivePeriod?.start
    ? dayjs(resource.effectivePeriod.start).format("HH:mm")
    : undefined;
  const end = resource?.effectivePeriod?.end
    ? dayjs(resource.effectivePeriod.end).format("HH:mm")
    : undefined;

  const periodText =
    start && end ? `${start} - ${end}` : start ? `A partir de ${start}` : "";

  const noteText =
    resource?.note && resource.note.length > 0 && resource.note[0]?.text
      ? resource.note[0].text
      : "";

  return {
    durationText,
    periodText,
    noteText,
    start,
    end,
  };
}

/** Temperatura da pele no sono (81264-4) e componentes. */
export function getSleepSkinTempDisplay(resource: any) {
  if (!resource) {
    return {
      minText: "-",
      avgText: "-",
      maxText: "-",
      minTime: "",
      maxTime: "",
    };
  }

  const mainValue = resource?.valueQuantity?.value;
  const mainUnit = resource?.valueQuantity?.unit || resource?.valueQuantity?.code;

  const components = Array.isArray(resource?.component) ? resource.component : [];

  const componentHasCode = (c: any, code: string) =>
    Array.isArray(c?.code?.coding) &&
    c.code.coding.some((coding: any) => String(coding?.code) === code);

  const minComponent = components.find(
    (c: any) =>
      componentHasCode(c, "8320-4") ||
      c?.code?.text?.toLowerCase().includes("mínima")
  );

  const maxComponent = components.find(
    (c: any) =>
      componentHasCode(c, "8315-4") ||
      c?.code?.text?.toLowerCase().includes("máxima")
  );

  const minTimeComponent = components.find(
    (c: any) =>
      componentHasCode(c, "sleep-skin-temp-min-time") ||
      c?.code?.text?.toLowerCase().includes("horário da temperatura mínima da pele")
  );

  const maxTimeComponent = components.find(
    (c: any) =>
      componentHasCode(c, "sleep-skin-temp-max-time") ||
      c?.code?.text?.toLowerCase().includes("horário da temperatura máxima da pele")
  );

  const formatTemp = (value: number | undefined, unit: string | undefined) => {
    if (value === undefined) return "-";
    if (!unit) return `${value.toFixed(1)}`;
    return `${value.toFixed(1)} ${unit}`;
  };

  const formatTimeFromComponent = (component: any) => {
    if (!component) return "";

    const rawValue: string | undefined =
      component.valueDateTime ?? component.valueTime ?? component.valueString;

    if (!rawValue || typeof rawValue !== "string") return "";

    const str = String(rawValue).trim();

    const parsed = dayjs(str);
    if (parsed.isValid()) {
      return parsed.format("HH:mm");
    }

    const match = str.match(/T(\d{1,2}):(\d{2})/);
    if (match) {
      const h = match[1].padStart(2, "0");
      const m = match[2];
      return `${h}:${m}`;
    }

    return "";
  };

  const minValue = minComponent?.valueQuantity?.value as number | undefined;
  const minUnit =
    minComponent?.valueQuantity?.unit || minComponent?.valueQuantity?.code;

  const maxValue = maxComponent?.valueQuantity?.value as number | undefined;
  const maxUnit =
    maxComponent?.valueQuantity?.unit || maxComponent?.valueQuantity?.code;

  const minTime = formatTimeFromComponent(minTimeComponent);
  const maxTime = formatTimeFromComponent(maxTimeComponent);

  const minText = minValue !== undefined ? formatTemp(minValue, minUnit) : "-";
  const avgText =
    mainValue !== undefined ? formatTemp(mainValue, mainUnit) : "-";
  const maxText = maxValue !== undefined ? formatTemp(maxValue, maxUnit) : "-";

  return {
    minText,
    avgText,
    maxText,
    minTime,
    maxTime,
  };
}

/**
 * Formata valor de exibição de uma Observation FHIR (wearable / mesmo conjunto do diálogo de dispositivos).
 */
export function formatWearableObservationValue(resource: any): string {
  if (!resource) return "-";

  if (resource?.code?.coding?.[0]?.code === "93832-4") {
    const { durationText, periodText, noteText } = getSleepTotalDisplay(resource);

    const pieces = [durationText];
    if (periodText) {
      pieces.push(`(${periodText})`);
    }
    if (noteText) {
      pieces.push(noteText);
    }

    return pieces.join(" | ");
  }

  if (resource?.code?.coding?.[0]?.code === "81264-4") {
    const { minText, avgText, maxText, minTime, maxTime } =
      getSleepSkinTempDisplay(resource);

    const minPart = minTime ? `Mín ${minText} (${minTime})` : `Mín ${minText}`;
    const avgPart = `Méd ${avgText}`;
    const maxPart = maxTime ? `Máx ${maxText} (${maxTime})` : `Máx ${maxText}`;

    return `${minPart} | ${avgPart} | ${maxPart}`;
  }

  if (resource?.code?.coding?.[0]?.code === "85354-9") {
    const sistolica =
      resource?.component?.[0]?.valueQuantity?.value ?? undefined;
    const diastolica =
      resource?.component?.[1]?.valueQuantity?.value ?? undefined;

    if (sistolica !== undefined && diastolica !== undefined) {
      return `${sistolica}/${diastolica} mmHg`;
    }
  }

  if (resource?.valueCodeableConcept) {
    return (
      resource.valueCodeableConcept?.coding?.[0]?.display ||
      resource.valueCodeableConcept?.text ||
      "-"
    );
  }

  if (resource?.valueQuantity) {
    const value = resource.valueQuantity.value;
    const unit = resource.valueQuantity.unit;
    const code = resource.valueQuantity.code;

    if (
      unit === "count" ||
      code === "1" ||
      unit === UCUM_STEPS ||
      code === UCUM_STEPS
    ) {
      if (value !== undefined) {
        return `${value}`;
      }
    }

    if (code === "kg" && typeof value === "number") {
      return `${value.toFixed(1)} kg`;
    }

    if ((unit === "%" || code === "%") && typeof value === "number") {
      return `${value.toFixed(1)} %`;
    }

    if (
      (unit === "beats/min" || code === "/min") &&
      typeof value === "number"
    ) {
      return `${value} bpm`;
    }

    if (
      unit === "min" ||
      unit === "s" ||
      code === "min" ||
      code === "s"
    ) {
      const durationUnit = unit || code;
      if (value === undefined || !durationUnit) return "-";

      if (durationUnit === "min") {
        const totalMinutes = Math.round(value as number);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours > 0) return `${hours}h ${minutes}min`;
        return `${minutes}min`;
      }

      if (durationUnit === "s") {
        const totalSeconds = Math.round(value as number);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (minutes > 0) return `${minutes}min ${seconds}s`;
        return `${seconds}s`;
      }
    }

    if (value !== undefined && unit) return `${value} ${unit}`;
    if (value !== undefined) return `${value}`;
    if (unit) return unit;
  }

  if (resource?.valueString) {
    return resource.valueString;
  }

  if (resource?.valueInteger !== undefined) {
    return resource.valueInteger.toString();
  }

  return "-";
}
